// Shared helpers for the event-analytics timelines (overview row, per-resource
// lanes, and the incident sub-timeline). They all map a time onto a horizontal
// track, so the positioning maths, axis labels and severity colours live here
// to keep the three components in sync.

// Parse a date-ish value (ISO string, epoch ms, Date) into epoch ms, or null
// when it's missing/invalid.
export function validMs(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

// Reduce a list of date candidates with a min/max reducer, falling back when
// none of them are valid.
export function firstMs(values, fallback, reducer) {
  const parsed = values.map((v) => validMs(v)).filter((v) => v !== null);
  if (!parsed.length) return fallback;
  return reducer.apply(Math, parsed);
}

// Map an incident/anomaly severity to its solid bar background colour.
// error = red, warning = amber, info = sky (distinct from the cyan service /
// emerald event phase colours and the red/amber stress severities).
export function severityBg(severity) {
  if (severity === 'error') return 'bg-red-500';
  if (severity === 'info') return 'bg-sky-400';
  return 'bg-amber-400';
}

// Severity ordering used when several anomalies are active at the same instant:
// error wins over warning wins over info. Unknown severities rank as warning so
// they still colour the track (matching severityBg's amber fallback).
export function severityRank(severity) {
  if (severity === 'error') return 3;
  if (severity === 'info') return 1;
  return 2;
}

// Short human label for a severity ('error' reads as "critical" for continuity
// with the existing UI wording).
export function severityLabel(severity) {
  if (severity === 'error') return 'critical';
  if (severity === 'info') return 'info';
  return 'warning';
}

// Pill/badge background + text colour for a severity.
export function severityBadgeClass(severity) {
  if (severity === 'error') return 'bg-red-500/20 text-red-200';
  if (severity === 'info') return 'bg-sky-500/20 text-sky-200';
  return 'bg-amber-500/20 text-amber-200';
}

// Small status-dot colour for a severity.
export function severityDotClass(severity) {
  if (severity === 'error') return 'bg-red-500';
  if (severity === 'info') return 'bg-sky-400';
  return 'bg-amber-400';
}

// Axis-label alignment so labels near the edges don't overflow the track.
export function alignForLeft(leftPercent) {
  if (leftPercent <= 8) return 'left';
  if (leftPercent >= 92) return 'right';
  return 'center';
}

export function axisMarkClass(align) {
  if (align === 'left') return 'translate-x-0 items-start';
  if (align === 'right') return '-translate-x-full items-end';
  return '-translate-x-1/2 items-center';
}

// Drop axis marks that would render within 1% of the previous one (keeps the
// first mark and any sufficiently spaced ones).
export function dedupeMarks(marks) {
  return marks.filter((mark, index, arr) => index === 0 || Math.abs(mark.left - arr[index - 1].left) > 1);
}

// A time -> percent scale for a fixed [start, end] window.
//
//   percent(ms)        -> position of an instant, clamped to 0..100
//   bar(startMs, endMs) -> { left, width, right } for an interval, where width
//                          is never thinner than `minWidth` so that short or
//                          instantaneous events stay visible.
export function createTimelineScale({ start, end, minWidth = 0.8 }) {
  const dur = end - start > 0 ? end - start : 1;
  const percent = (ms) => Math.max(0, Math.min(100, ((ms - start) / dur) * 100));
  const bar = (startMs, endMs) => {
    const left = percent(startMs);
    const width = Math.max(percent(Math.max(endMs, startMs)) - left, minWidth);
    return { left, width, right: left + width };
  };
  return { start, end, dur, percent, bar };
}
